import React, { useState } from "react";
import { Dialog } from "../containers/Dialog.tsx";
import { TextInput } from "../inputs/TextInput.tsx";
import { Button } from "../widgets/Button.tsx";
import { Exercise } from "../../api/types.ts";
// import { Select } from "../Select.tsx";

interface SetDialogProps {
  open: boolean;
  closeDialog: () => void;
  resistanceUnit: "kg" | "lbs";
  exercise: Exercise;
  initialRepetitions?: string;
  initialResistance: string;
  editMode?: boolean;
  onAddSet: (repetitions: number, resistance: number) => void;
  onEditSet: (repetitions: number, resistance: number) => void;
}

const SetDialog: React.FC<SetDialogProps> = ({
  open,
  closeDialog,
  // workoutExerciseId,
  // unit,
  initialRepetitions = "",
  initialResistance = "",
  exercise,
  resistanceUnit,
  onAddSet,
  onEditSet,
  editMode = false,
}) => {
  const [repetitions, setRepetitions] = useState(initialRepetitions);
  const [resistance, setResistance] = useState(initialResistance);
  // const [selectedUnit, setSelectedUnit] = useState(unit);
  const [errors, setErrors] = useState<{
    repetitions?: string;
    resistance?: string;
  }>({});

  const handleClose = () => {
    setRepetitions("");
    setResistance("");
    setErrors({});
    closeDialog();
  };

  const handleDelete = () => {
    // For simplicity, we'll just call onEditSet with zero values to indicate deletion
    if (editMode && onEditSet) {
      onEditSet(0, 0);
    }
    handleClose();
  };

  const handleSave = () => {
    const newErrors: { repetitions?: string; resistance?: string } = {};

    if (
      !repetitions ||
      isNaN(Number(repetitions)) ||
      Number(repetitions) <= 0
    ) {
      newErrors.repetitions = "Please enter a valid number of repetitions";
    }

    if (!resistance || isNaN(Number(resistance))) {
      newErrors.resistance = "Please enter a valid resistance value";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (editMode && onEditSet) {
      onEditSet(Number(repetitions), Number(resistance));
    } else {
      onAddSet(Number(repetitions), Number(resistance));
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={
        editMode
          ? `Edit Set for ${exercise.name}`
          : `Add Set for ${exercise.name}`
      }
    >
      <Dialog.Content>
        <TextInput
          label="Repetitions"
          type="number"
          value={repetitions}
          onChange={(e) => setRepetitions(e.target.value)}
          placeholder="Enter number of repetitions"
          required
          errorText={errors.repetitions}
        />
        <TextInput
          label={<span>Resistance</span>}
          infoText={
            exercise.start_resistance === 0
              ? "Set the resistance to 0 if you are using your body weight."
              : `Excluding the start resistance of ${exercise.start_resistance}${resistanceUnit}.`
          }
          type="number"
          value={resistance}
          onChange={(e) => setResistance(e.target.value)}
          placeholder="Enter resistance"
          required
          errorText={errors.resistance}
          rightElement={<span>{resistanceUnit}</span>}
        />
        {exercise.efficiency_factor < 1.0 && (
          <span className="italic font-light text-xs">
            The efficiency factor of {100 * exercise.efficiency_factor}% will be
            applied automatically
          </span>
        )}
      </Dialog.Content>
      <Dialog.Actions>
        {editMode && (
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        )}
        <Button variant="submit" onClick={handleSave}>
          Save
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export { SetDialog };
